import { APIURL, apiGet, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, toggleWidgetTheme, normalizeWidgetTheme, getEChartsTooltipTheme, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface TradeTypeItem {
    name: string;
    value: number;
}

/** Palette matches the design: purchase = yellow, sale = purple. */
const TRADE_TYPE_COLORS = [
    '#FFC01E', '#7B2FF7', '#FF7A1A', '#22C55E', '#A3E635',
    '#3B82F6', '#EC4899', '#14B8A6', '#8B6CFF', '#6366F1'
];

/** Fixed color per trade type so the legend stays stable across symbols. */
const TRADE_TYPE_COLOR_MAP: Record<string, string> = {
    Purchase: '#FFC01E',
    Sale: '#7B2FF7',
    Exchange: '#FF7A1A'
};

/** Disclosure feeds abbreviate the transaction type in a few different ways. */
const TRADE_TYPE_LABELS: Record<string, string> = {
    p: 'Purchase',
    purchase: 'Purchase',
    buy: 'Purchase',
    s: 'Sale',
    sale: 'Sale',
    sell: 'Sale',
    'sale (full)': 'Sale',
    'sale (partial)': 'Sale',
    e: 'Exchange',
    exchange: 'Exchange'
};

export class TraderverseESGSectorsperfomancedistribution {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private currentData: TradeTypeItem[] = [];
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private hiddenTypes: Set<string> = new Set();
    private filters = {
        symbol: '' as string | undefined
    };

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);
        this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });
        this.init();
        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            isSymbolSelectable: true,
            symbol: 'AAPL'
        };
    }

    private applyConfigFilters(): void {
        if (this.config.symbol) this.filters.symbol = this.config.symbol;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        cancelSelectEvent(this.shadowRoot);
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} ESG Sectors Perfomance Distribution</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
                    <i class="pi pi-ellipsis-v"></i>
                </div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    <div class="item">
                        <div class="legendsCollection" id="trade-type-legends"></div>
                    </div>
                    <span></span>
                    ${this.config.hideSymbol ? '' : `<div class="item dropStyle01">
                        <div class="trader-sm-card" id="selected-symbol">
                            ${this.config.symbol
                                ? getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol, type: 'stock', name: '' })
                                : '<span>Select Symbol</span>'}
                        </div>
                        <span id="symbol-dropdown-placeholder"></span>
                    </div>`}
                </div>
                <div class="chartDv" id="houseTradeTypesContainer"></div>
            </div>
        </div>`;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        setupHeaderPin(this.shadowRoot, this.config);

        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) {
                placeholder.replaceWith(dropdown);
            }
        }
    }

    private onSelectChange(stock: any): void {
        this.changeSymbol(stock.symbol, stock);
    }

    private buildPayload(): Record<string, unknown> {
        const payload: Record<string, unknown> = { page: 1, count: 500, chamber: 'house' };
        if (this.filters.symbol) payload.symbol = this.filters.symbol;
        return payload;
    }

    private extractRows(json: any): any[] {
        const result = json?.data ?? json;
        if (Array.isArray(result?.data)) return result.data;
        if (Array.isArray(result)) return result;
        return [];
    }

    /** Only keep rows disclosed by the House chamber. */
    private isHouseRow(row: any): boolean {
        const value = String(
            row?.chamber ?? row?.house ?? row?.member_of ?? row?.body ?? ''
        ).toLowerCase();
        // When the feed does not label the chamber we keep the row rather than drop everything.
        if (!value) return true;
        return value.includes('house') || value.includes('rep');
    }

    private resolveTradeType(row: any): string {
        const typeKey = Object.keys(row ?? {}).find(key => /^(transaction_)?type$/i.test(key))
            ?? Object.keys(row ?? {}).find(key => /type/i.test(key) && !/asset/i.test(key));
        const raw = typeKey ? String(row[typeKey] ?? '').trim() : '';
        if (!raw || raw === '-' || raw === '--' || raw.toLowerCase() === 'null') return 'Undisclosed';
        return TRADE_TYPE_LABELS[raw.toLowerCase()] ?? raw.replace(/\b\w/g, c => c.toUpperCase());
    }

    private aggregateTradeTypes(rows: any[]): TradeTypeItem[] {
        const counts = new Map<string, number>();
        rows.filter(row => this.isHouseRow(row)).forEach(row => {
            const label = this.resolveTradeType(row);
            counts.set(label, (counts.get(label) ?? 0) + 1);
        });
        return Array.from(counts.entries())
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }

    /** Temporary sample data used while the trade-types API returns nothing. */
    private getDummyData(): TradeTypeItem[] {
        return [
            { name: 'Purchase', value: 752 },
            { name: 'Sale', value: 248 }
        ];
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
            script.async = true;
            script.onload = () => resolve(true);
            this.shadowRoot.appendChild(script);
        });
    }

    private async setupChart(symbol?: string): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const container = this.shadowRoot.querySelector('#houseTradeTypesContainer');
        if (widget) widget.classList.add('loading');

        if (symbol) {
            this.filters.symbol = symbol;
            this.config.symbol = symbol;
        }

        try {
            const res = await apiGet({
                method: 'POST',
                url: `${APIURL}/v1/discover/congress_trading`,
                body: this.buildPayload(),
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!res.ok) throw new Error('Failed to fetch congress trading data');

            const json = await res.json();
            this.currentData = this.aggregateTradeTypes(this.extractRows(json));
            this.hiddenTypes.clear();

            // API currently returns nothing — fall back to sample data until it is live.
            if (!this.currentData.length) {
                this.currentData = this.getDummyData();
            }

            container?.setAttribute('data-testing-id', 'house-trade-types');
            this.renderChart();

            this.emit('dataUpdate', json);
        } catch (err) {
            console.error('Failed to fetch/setup chart data, showing dummy data:', err);
            this.currentData = this.getDummyData();
            this.hiddenTypes.clear();
            container?.setAttribute('data-testing-id', 'house-trade-types');
            this.renderChart();
            this.emit('error', err);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!this.currentData?.length || !(window as any).echarts) {
            return;
        }
        this.renderChart();
    }

    private renderChart(): void {
        const container = this.shadowRoot.querySelector('#houseTradeTypesContainer');
        if (!container) return;

        container.innerHTML = `<div id="houseTradeTypesChart" class="chart coverDv"></div>`;
        this.drawChart();
        this.renderLegends();
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#houseTradeTypesChart') as HTMLElement;
        if (!container || !this.currentData?.length) return;

        if (this.currentChart) {
            this.currentChart.dispose();
        }

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');
        const bgColor = getComputedStyle(widgetBox).getPropertyValue('--base-dark');

        const option = {
            backgroundColor: 'transparent',
            legend: { show: false },
            tooltip: {
                trigger: 'item',
                ...getEChartsTooltipTheme(widgetBox),
                padding: 10,
                formatter: (p: any) => `
                  <div style="display:flex;flex-direction:column;align-items:center;gap:2px;min-width:70px">
                    <span style="width:9px;height:9px;border-radius:50%;background:${p.color};margin-bottom:4px"></span>
                    <span style="font-weight:700;font-size:15px">${Number(p.percent).toFixed(1)}%</span>
                    <span style="opacity:.6">${p.name}</span>
                  </div>
                `
            },
            series: [
                {
                    type: 'pie',
                    radius: '90%',
                    center: ['50%', '50%'],
                    startAngle: 90,
                    minAngle: 4,
                    avoidLabelOverlap: true,
                    itemStyle: {
                        borderColor: bgColor,
                        borderWidth: 2
                    },
                    label: {
                        show: true,
                        position: 'inside',
                        formatter: '{d}%',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        fontSize: fontSize
                    },
                    labelLine: { show: false },
                    emphasis: {
                        scale: false,
                        itemStyle: { shadowBlur: 12, shadowColor: 'rgba(0, 0, 0, 0.35)' }
                    },
                    data: this.currentData.map((d, index) => ({
                        ...d,
                        itemStyle: { color: this.getTradeTypeColor(d.name, index) }
                    }))
                }
            ]
        };

        this.currentChart = echarts.init(container);
        this.currentChart.setOption(option);

        this.hiddenTypes.forEach(name => {
            this.currentChart.dispatchAction({ type: 'legendUnSelect', name });
        });
    }

    private getTradeTypeColor(name: string, index: number): string {
        return TRADE_TYPE_COLOR_MAP[name] ?? TRADE_TYPE_COLORS[index % TRADE_TYPE_COLORS.length];
    }

    private renderLegends(): void {
        const container = this.shadowRoot.querySelector('#trade-type-legends') as HTMLElement;
        if (!container) return;

        container.innerHTML = '';

        this.currentData.forEach((item, index) => {
            const el = document.createElement('span');
            const isActive = !this.hiddenTypes.has(item.name);
            el.className = `toggle-owner${isActive ? ' active' : ''}`;
            el.style.backgroundColor = this.getTradeTypeColor(item.name, index);
            el.setAttribute('data-tooltip', item.name);
            el.title = item.name;

            el.addEventListener('click', () => {
                if (!this.currentChart) return;
                const hide = !this.hiddenTypes.has(item.name);
                if (hide) {
                    this.hiddenTypes.add(item.name);
                } else {
                    this.hiddenTypes.delete(item.name);
                }
                this.currentChart.dispatchAction({
                    type: hide ? 'legendUnSelect' : 'legendSelect',
                    name: item.name
                });
                el.classList.toggle('active', !hide);
            });

            container.appendChild(el);
        });
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(
            this.shadowRoot,
            this.config,
            stock,
            stockId,
            (symbol: string) => this.setupChart(symbol),
            (type: any, data: any) => this.emit(type, data)
        );
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }

    public destroy(): void {
        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global { interface Window { TraderverseESGSectorsperfomancedistribution: typeof TraderverseESGSectorsperfomancedistribution; } }
if (typeof window !== 'undefined') window.TraderverseESGSectorsperfomancedistribution = TraderverseESGSectorsperfomancedistribution;
